import React, { useState } from 'react';
import Button from '../../Button';
import EmptyState from '../../EmptyState';
import { READING, figureOrDash, reading } from '../../../domain/reading';
import ConfirmDialog from '../../ConfirmDialog';
import { Menu, MenuItem } from '../../Menu';
import AddTagChooser from './AddTagChooser';
import TagFormDrawer from './TagFormDrawer';
import { SWATCHES } from '../../../domain/categories';
import {
  TAG_KIND_LABELS, TAGS_WORDING as W, sortTags, tagKind, tagErrors, emptyTagDraft, tagDraftOf,
  tagCountLabel, tagMemberNames, tagDeleteMessage,
} from '../../../domain/tags';
import s from './tags.module.css';

// Students → Tags. A managed collection, edited here (#138).
//
// MMS's tab is a grid with the furniture of its other lists — Add Tag,
// Actions, Columns, Sort, Search — and three columns:
//
//   Name      with the kind as a badge underneath, not as its own column
//   Color     the swatch chip
//   Students  the members by name, not as a count
//
// The row menu is Edit and Delete; the toolbar's `Actions` is a bulk menu over
// ticked rows. This builds Add Tag, the grid and the row menu; Columns, Sort,
// Search and the bulk Actions menu are not here (#138).
//
// Deleting is not gated, as in MMS, whose confirm reads "Are you sure you want
// to delete Piano from your tags?" with no count, warning or reassignment: a
// tag is a label on students, and membership lives on the tag document, so
// removing it takes nothing with it.
//
// Ours: a tag with no members shows "No students" rather than an empty cell,
// which in a column of names would read as a rendering fault.

// The drafts are `emptyTagDraft` and `tagDraftOf` in src/domain/tags.js.

// `loaded`: the tags have been read (`useTags().loaded`). Until then the count
// is the dash and the empty state the loading state (#629, #637).
export default function TagsTab({
  loaded = true,
  // The tags' read stopped before it answered (#647).
  failed = false,
  tags = [],
  students = [],
  nameOf = (st) => st?.id,
  onSave,
  onRemove,
}) {
  const [chooserOpen, setChooserOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [errors, setErrors] = useState({});
  const [menuFor, setMenuFor] = useState(null);
  const [confirm, setConfirm] = useState(null);
  // Held over the write, not over the snapshot that follows it: two clicks on
  // Save before the first resolves would be two records, and the duplicate
  // refusal compares against a collection that has not heard about the first
  // yet. `CategoriesDrawer` closes the same hole with its own `busy`.
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  const rows = sortTags(tags);

  // A field being fixed stops being red as it is fixed, as in
  // CategoriesDrawer.
  const changeDraft = (next) => {
    setDraft(next);
    setErrors((prev) => (
      Object.keys(prev).length && next.label !== draft?.label ? {} : prev
    ));
  };

  const openAdd = (kind) => {
    setChooserOpen(false);
    setErrors({});
    setDraft(emptyTagDraft(kind));
  };

  const openEdit = (tag) => {
    setMenuFor(null);
    setErrors({});
    setDraft(tagDraftOf(tag));
  };

  const closeForm = () => { setDraft(null); setErrors({}); };

  // Save stays reachable and the refusal arrives on submit (#116). The blank
  // name still greys the button, as on the category drawer.
  const save = async () => {
    const label = (draft.label || '').trim();
    if (!label) return;
    const refusals = tagErrors({ ...draft, label }, tags);
    setErrors(refusals);
    if (Object.keys(refusals).length) return;
    // `showInactive` is a property of the drawer, not of the tag, and must not
    // reach the document. The provider writes named fields rather than
    // spreading the draft, so this is a second guard.
    await run(() => onSave({
      id: draft.id,
      label,
      kind: draft.kind,
      color: draft.color,
      description: draft.description,
      studentIds: draft.studentIds,
    }));
    closeForm();
  };

  const members = (tag) => {
    const names = tagMemberNames(tag, students, nameOf);
    // An id naming a student who no longer exists is dropped rather than shown
    // as a blank: nothing removes a deleted student from the tags they were on
    // (see the note in firestore.rules).
    if (!names.length) return <span className={s.noMembers}>{W.noStudents}</span>;
    return names.join(', ');
  };

  return (
    <>
      <div className={s.bar}>
        <span className={s.count}>
          {figureOrDash(loaded || rows.length > 0, tagCountLabel(rows.length))}
        </span>
        <span className={s.spacer} />
        <Button onClick={() => setChooserOpen(true)} disabled={busy}>{W.addTag}</Button>
      </div>

      {rows.length === 0 && !loaded ? (
        <EmptyState reading={failed ? READING.failed : READING.unread} />
      ) : rows.length === 0 ? (
        <div className={s.card}>
          {/* MMS's own empty state, near enough: "You don't have any tags yet /
              Keep your students organized by assigning them tags". */}
          <EmptyState
            reading={reading(loaded, rows.length)}
            title={W.emptyTitle}
            description={W.emptyDescription}
            actionLabel={W.addTag}
            onAction={() => setChooserOpen(true)}
          />
        </div>
      ) : (
        <div className={s.card}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>{W.columnName}</th>
                <th>{W.columnColor}</th>
                <th>{W.columnStudents}</th>
                <th className={s.menuCell} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((tag) => (
                <tr key={tag.id}>
                  <td>
                    <div className={s.name}>{tag.label}</div>
                    <span className={s.kindBadge}>{TAG_KIND_LABELS[tagKind(tag)]}</span>
                    {tag.description ? (
                      <div className={s.description}>{tag.description}</div>
                    ) : null}
                  </td>
                  <td>
                    <span className={s.swatchCell}>
                      <span className={s.swatch} style={{ background: tag.color || SWATCHES[0] }} />
                    </span>
                  </td>
                  <td className={s.members}>{members(tag)}</td>
                  <td className={s.menuCell}>
                    <Menu
                      open={menuFor === tag.id}
                      onClose={() => setMenuFor(null)}
                      trigger={(
                        <button
                          className={s.kebab}
                          aria-label={`Actions for ${tag.label}`}
                          disabled={busy}
                          onClick={() => setMenuFor(menuFor === tag.id ? null : tag.id)}
                        >
                          ⋮
                        </button>
                      )}
                    >
                      <MenuItem icon="pencil" onClick={() => openEdit(tag)}>{W.edit}</MenuItem>
                      <MenuItem
                        icon="trash"
                        onClick={() => { setMenuFor(null); setConfirm(tag); }}
                      >
                        {W.delete}
                      </MenuItem>
                    </Menu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AddTagChooser
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        onChoose={openAdd}
      />

      <TagFormDrawer
        open={Boolean(draft)}
        draft={draft}
        onChange={changeDraft}
        onSave={save}
        onClose={closeForm}
        students={students}
        nameOf={nameOf}
        errors={errors}
        busy={busy}
      />

      {/* MMS's wording, and MMS's silence about the students on it. */}
      <ConfirmDialog
        open={Boolean(confirm)}
        title={W.deleteTitle}
        message={tagDeleteMessage(confirm?.label)}
        confirmLabel={W.delete}
        discardLabel={W.cancel}
        cancelLabel=""
        onDiscard={() => setConfirm(null)}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => { await run(() => onRemove(confirm.id)); setConfirm(null); }}
      />
    </>
  );
}
